import { normaliseText } from '@1trading/ts-common/utils';
import { z } from 'zod';

import i18n from '@/lib/i18n';
import { createPostalCodeRefinement, mainstreamCountrySchema } from '@/services/api/mainstream';
import { type InstitutionDetailsV2Address, institutionDetailsV2AddressSchema } from '@/services/api/profile';
import { blankAsAbsent } from '@/utils/blank-as-absent';

const { refinement: postalCodeRefinement, params: postalCodeRefinementParams } = createPostalCodeRefinement<Partial<Pick<InstitutionAddress, 'country' | 'postalCode'>>>({
  getParts: (data) => ({ country: data?.country, postCode: data?.postalCode }),
  message: i18n.t('institutionDetails.address.errors.postalCode'),
  path: ['postalCode'],
});

export const institutionAddressSchema = z
  .object({
    addressLine1: institutionDetailsV2AddressSchema.shape.addressLines.element,
    addressLine2: blankAsAbsent(institutionDetailsV2AddressSchema.shape.addressLines.element),
    locality: institutionDetailsV2AddressSchema.shape.locality,
    postalCode: institutionDetailsV2AddressSchema.shape.postalCode,
    country: mainstreamCountrySchema(i18n.t('institutionDetails.address.errors.country')),
  })
  .refine(postalCodeRefinement, postalCodeRefinementParams);

export type InstitutionAddress = z.infer<typeof institutionAddressSchema>;

export const INSTITUTION_ADDRESS_DEFAULTS = {
  addressLine1: '',
  addressLine2: '',
  locality: '',
  postalCode: '',
  country: '',
} satisfies InstitutionAddress;

export const institutionAddressToInstitutionDetailsAddressApi = ({ addressLine1, addressLine2, ...address }: InstitutionAddress): InstitutionDetailsV2Address => ({
  ...address,
  // Raw form values still carry a blank second line, which the schema reads as absent
  addressLines: [addressLine1, ...(addressLine2 && normaliseText(addressLine2) ? [addressLine2] : [])].map((line) => line.replace(/,/g, '')),
});

export const institutionDetailsAddressApiToInstitutionAddress = ({ addressLines, ...address }: InstitutionDetailsV2Address): InstitutionAddress => ({
  ...address,
  addressLine1: addressLines[0] ?? '',
  addressLine2: addressLines[1] ?? '',
});
